import { Go, Table, TriageFlow, type GuideSection } from './parts'

/**
 * Guide utilisateur — version française.
 *
 * La prose vit ici plutôt que dans le catalogue de traductions : treize
 * sections de texte suivi, avec des liens vers les écrans et des passages en
 * gras, se relisent mal découpées en centaines de clés. Le jumeau anglais est
 * `sections.en.tsx` ; les deux fichiers ont les mêmes `id` de section, pour
 * qu'un lien envoyé à un collègue reste valable dans l'autre langue.
 */
export const SECTIONS_FR: GuideSection[] = [
  {
    id: 'a-quoi-sert',
    titleKey: 'guide.s.purpose',
    body: (
      <>
        <p>
          Le Compliance Crawler surveille en continu qui utilise les moyens de paiement InTouch, et comment. Il visite
          des sites, en garde des captures, les note, et dépose dans une file de travail tout ce qui mérite un œil
          humain. Il ne décide rien seul : il prépare le dossier, vous tranchez.
        </p>
        <Table
          head={['Domaine', 'La question posée']}
          rows={[
            [<Go to="merchants">Marchands</Go>, 'Nos marchands partenaires sont-ils toujours conformes à ce qu’ils ont déclaré ?'],
            [<Go to="discovery">Usurpation de marques</Go>, 'Qui crée des domaines imitant InTouch, Wave, Orange Money ou un autre opérateur ?'],
            [<Go to="webscan">Marchands non déclarés</Go>, 'Qui encaisse avec nos moyens de paiement sans être autorisé pour cette marque ?'],
            [<Go to="ads">Détection pubs</Go>, 'Qui fait de la publicité, pour quoi, et avec quel statut fiscal ?'],
            [<Go to="betting">Paris & jeux</Go>, 'Les sites de paris acceptent-ils des paiements là où c’est interdit ?'],
          ]}
        />
        <p>
          Ce guide s'adresse à tous ceux qui ouvrent l'outil : l'agent qui traite les détections au quotidien, le
          responsable qui lance les scans et configure le vocabulaire de recherche, l'administrateur qui gère les
          comptes. Chaque section indique qui peut faire quoi.
        </p>
      </>
    ),
  },
  {
    id: 'premiers-pas',
    titleKey: 'guide.s.start',
    body: (
      <>
        <p>
          Il n'y a pas d'inscription : votre compte est créé par un administrateur, qui vous transmet votre adresse et
          un mot de passe.
        </p>
        <p>
          <strong>Changer votre mot de passe.</strong> Aucun écran ne le permet aujourd'hui. Demandez à un
          administrateur, qui le réinitialise depuis <Go to="users">Utilisateurs</Go>. Ne le partagez pas pour autant —
          signalez simplement qu'il doit être changé.
        </p>
        <p>
          <strong>Si vous ne voyez presque rien</strong>, ce n'est pas une panne, c'est votre périmètre. Deux réglages,
          posés par un administrateur sur votre compte, décident de tout ce qui suit :
        </p>
        <ul>
          <li>
            <strong>les pays</strong> — vous ne voyez que les détections des pays qui vous sont attribués. Le message
            « Aucun pays n'est associé à votre compte » signifie que votre compte n'en a aucun en commun avec votre
            organisation ;
          </li>
          <li>
            <strong>les modules</strong> — chaque entrée du menu correspond à un module. Un module absent de votre
            compte, ou auquel votre organisation n'est pas abonnée, ne s'affiche pas du tout.
          </li>
        </ul>
        <p className="guide-callout">
          Dans les deux cas, une liste vide signifie « tout ce que permet votre organisation », et non « rien ».
        </p>
        <p>
          <strong>En bas du menu</strong> : le choix de la langue et du thème, votre nom avec votre rôle, et la
          déconnexion. Le menu lui-même se rabat pour gagner de la place.
        </p>
      </>
    ),
  },
  {
    id: 'le-menu',
    titleKey: 'guide.s.menu',
    body: (
      <>
        <p>Le menu latéral est groupé en quatre sections. Les entrées marquées d'un triangle se déplient en sous-écrans.</p>
        <Table
          head={['Entrée', 'Ce que vous y trouvez', 'Qui la voit']}
          rows={[
            [<Go to="overview">Vue d'ensemble</Go>, 'Les quatre métriques du jour, deux graphiques, l’état de la file', 'module Marchands'],
            [<Go to="merchants">Marchands</Go>, 'Portefeuille · Rapports de crawl', 'module Marchands'],
            [<Go to="discovery">Usurpation de marques</Go>, 'Les domaines, apps et comptes imitant une marque', 'module Usurpation'],
            [<Go to="webscan">Marchands non déclarés</Go>, 'Le scan mondial et sa carte', 'module Scan mondial'],
            [<Go to="ads">Détection pubs</Go>, 'Annonceurs · Bannières · Campagnes · Requêtes', 'module Publicité'],
            [<Go to="betting">Paris & jeux</Go>, 'Tests de paiement · Services · Recherche · À qualifier · Référentiels', 'module Paris'],
            [<Go to="brands">Modèles de recherche</Go>, 'Les marques surveillées et leur vocabulaire', 'module Marques'],
            [<Go to="rules">Règles pays</Go>, 'Ce qui est interdit, encadré ou signalé, pays par pays', 'module Règles'],
            [<Go to="users">Utilisateurs</Go>, 'Les comptes et leurs périmètres', 'administrateurs'],
            [<Go to="orgs">Organisations</Go>, 'Les organisations clientes et leurs abonnements', 'super admin'],
          ]}
        />
        <p>
          Trois entrées portent un compteur quand du travail attend : <strong>Annonceurs</strong>,{' '}
          <strong>Tests de paiement</strong> et <strong>À qualifier</strong>. C'est le meilleur point de départ le matin.
        </p>
        <p>
          Une nuance utile : le filtrage par module ne s'applique qu'aux agents. Un administrateur, un propriétaire ou
          un super admin voit toutes les entrées du menu. En revanche, l'abonnement de l'organisation s'impose à tous,
          sauf au super admin.
        </p>
      </>
    ),
  },
  {
    id: 'vue-d-ensemble',
    titleKey: 'guide.s.overview',
    body: (
      <>
        <p>C'est l'écran d'accueil, et il répond à une seule question : qu'est-ce qui attend quelqu'un aujourd'hui ?</p>
        <p>
          <strong>La bande du haut</strong> compte ce qui est ouvert. Quatre chiffres cliquables qui mènent directement
          à la file concernée : alertes ouvertes, marchands suspendus, usurpations à qualifier, marchands non déclarés à
          qualifier.
        </p>
        <p>
          <strong>Trois indicateurs de santé</strong> complètent la bande — marchands surveillés, crawls des dernières
          24 heures, détections traitées. Ils ne demandent aucune action : ils disent si la machine tourne.
        </p>
        <p>
          <strong>Deux graphiques.</strong> « Alertes par niveau » empile, jour par jour, les niveaux OK, WARNING et
          CRITICAL — c'est là qu'une dérive se voit avant d'être un problème. « Répartition par action » montre ce que
          les crawls ont recommandé.
        </p>
        <p>
          <strong>La barre de file</strong>, en bas, est l'état technique des travaux : en attente, actifs, terminés,
          échoués, différés. Un nombre d'échoués qui grimpe est à remonter à l'équipe technique — ce n'est pas quelque
          chose que vous pouvez corriger depuis l'interface.
        </p>
      </>
    ),
  },
  {
    id: 'marchands',
    titleKey: 'guide.s.merchants',
    body: (
      <>
        <h3>Le portefeuille</h3>
        <p>
          Le tableau se trie par n'importe quelle colonne. Quatre statuts : <strong>Actif</strong>,{' '}
          <strong>Suspendu</strong>, <strong>En révision</strong>, <strong>Résilié</strong>.
        </p>
        <p>
          <strong>Ajouter un marchand</strong> demande cinq champs : nom, URL, pays, catégorie déclarée et fréquence de
          crawl. La catégorie compte beaucoup : c'est elle qui sera comparée à ce que l'outil trouve réellement sur le
          site. Un marchand déclaré « commerce de détail » dont la page vend des paris sera signalé pour cet écart.
        </p>
        <p>
          <strong>Import CSV</strong> pour en charger plusieurs d'un coup. Les lignes sont prévisualisées avant
          écriture.
        </p>
        <Table
          head={['Action', 'Effet', 'Qui peut']}
          rows={[
            ['Scanner', 'Lance un crawl immédiat, avec une barre de progression', 'administrateur'],
            ['Modifier', 'Change les informations déclarées', 'tous'],
            ['Suspendre', 'Passe un marchand actif en suspendu', 'tous'],
            ['Réactiver', 'Repasse un marchand suspendu en actif', 'tous'],
            ['Suppr.', 'Efface le marchand, après confirmation. Irréversible', 'administrateur'],
          ]}
        />
        <h3>Les rapports de crawl</h3>
        <p>
          Chaque visite laisse un rapport, filtrable par niveau, pays et action. Le score va de 0 à 100 et décide du
          niveau :
        </p>
        <Table
          head={['Score', 'Niveau', 'Ce qu’il faut faire']}
          rows={[
            ['moins de 40', 'OK', 'Rien. La visite est conforme'],
            ['40 à 69', 'WARNING', 'À examiner'],
            ['70 et plus', 'CRITICAL', 'À traiter sans attendre — suspension, blocage ou retrait'],
          ]}
        />
        <p>
          <strong>Ouvrir un rapport</strong> donne tout ce qui a fondé ce score : les sous-scores, l'analyse de l'image
          par l'IA, les raisons une par une, les mots-clés de paiement, la capture d'écran du site au moment de la
          visite, et un extrait du texte visible.
        </p>
        <p>
          Deux indicateurs méritent une explication. <strong>SSL</strong> dit si le certificat était valide. Le{' '}
          <strong>drift score</strong> mesure l'écart entre la page du jour et la page de référence : proche de 1, le
          site n'a pas bougé ; en dessous de 0,4, il a changé de nature et le score de risque monte fortement. C'est le
          signal d'un site revendu ou détourné.
        </p>
      </>
    ),
  },
  {
    id: 'usurpation',
    titleKey: 'guide.s.impersonation',
    body: (
      <>
        <p>
          Cet écran rassemble tout ce qui imite une marque surveillée. L'outil ne cherche pas au hasard : il part des
          marques et du vocabulaire configurés dans <Go to="brands">Modèles de recherche</Go>.
        </p>
        <Table
          head={['Onglet', 'Source', 'Ce qu’elle repère']}
          rows={[
            ['Domaines', 'Registres publics de certificats SSL', 'Un domaine fraîchement créé au nom proche d’une marque. La source la plus précoce : souvent vu avant d’être en ligne'],
            ['Apps', 'Google Play et App Store', 'Une fausse application au nom d’une marque'],
            ['Sociaux', 'Recherche web et groupes Telegram', 'Un faux compte se présentant comme un service client officiel'],
            ['SMS', 'Signalements reçus d’utilisateurs', 'Un SMS frauduleux, avec le lien qu’il contenait'],
            ['Paiements', 'Pages de paiement analysées', 'Une intégration de paiement suspecte'],
          ]}
        />
        <p>
          <strong>Lire une fiche.</strong> Chaque détection porte la marque imitée, la façon dont la ressemblance a été
          établie, un score de similarité et un niveau de suspicion — LOW, MEDIUM, HIGH ou CRITICAL. Les fiches
          CRITICAL sont encadrées en rouge. Le niveau n'est pas une décision : c'est une priorité de lecture.
        </p>
        <p>
          La ressemblance est établie de quatre manières : le nom de la marque est <strong>contenu</strong> dans le
          domaine, c'est une <strong>faute de frappe</strong> proche, ce sont des{' '}
          <strong>caractères d'apparence identique</strong> venus d'un autre alphabet, ou c'est la marque{' '}
          <strong>accolée à un autre mot</strong> du genre « wave-money-sn ».
        </p>
        <p>
          <strong>Vérifier un domaine à la main.</strong> Un champ permet de coller une adresse et de la confronter aux
          domaines suspects déjà connus. Pratique quand on vous signale un lien.
        </p>
        <p>
          <strong>Lancer un scan</strong> est réservé aux administrateurs. Les scans tournent de toute façon seuls : les
          domaines toutes les heures, les stores et les réseaux sociaux deux fois par jour.
        </p>
      </>
    ),
  },
  {
    id: 'non-declares',
    titleKey: 'guide.s.undeclared',
    body: (
      <>
        <p>
          Ici, on ne cherche pas qui imite la marque, mais <strong>qui encaisse avec nos moyens de paiement sans y être
          autorisé</strong>. L'outil interroge le web pays par pays avec des formules du genre « payer avec InTouch »,
          visite les sites trouvés, et les confronte à votre base.
        </p>
        <Table
          head={['Verdict', 'Situation', 'Action proposée']}
          rows={[
            ['À onboarder', 'Site inconnu de la base, activité légitime', 'Le démarcher, ou le bloquer si l’activité est interdite'],
            ['À investiguer', 'Marchand connu, mais pas autorisé pour cette marque — autorisé Wave, par exemple, et trouvé en train d’encaisser via InTouch', 'Chercher le contrat manquant'],
            ['À bloquer', 'Site inconnu et activité interdite dans ce pays', 'Couper l’encaissement'],
          ]}
        />
        <p>Un marchand connu <strong>et</strong> autorisé pour la marque ne remonte pas : la surveillance des marchands s'en occupe déjà.</p>
        <p>
          <strong>La carte.</strong> Le planisphère colore chaque pays selon le nombre de marchands non déclarés
          trouvés : plus le pays est foncé, plus il y en a. Gris très clair, le pays n'est pas surveillé ; gris, il l'est
          mais rien n'a été trouvé. Cliquez sur un pays pour filtrer la liste.
        </p>
        <p className="guide-callout guide-callout-warn">
          <strong>Lisez le bandeau d'avertissement en haut, s'il apparaît.</strong> S'il signale qu'aucune autorisation
          marchand × marque n'est enregistrée, le croisement est inerte : <strong>tout</strong> remonte comme inconnu, y
          compris vos propres marchands. Les résultats sont alors des pistes à qualifier, pas des infractions
          constatées. La réponse n'est pas de traiter la liste, mais de compléter les autorisations dans{' '}
          <Go to="brands">Modèles de recherche</Go>.
        </p>
        <p>Le scan tourne une fois par jour, vers 3 h du matin, sur un pays différent chaque jour en rotation.</p>
      </>
    ),
  },
  {
    id: 'pubs',
    titleKey: 'guide.s.ads',
    body: (
      <>
        <p>
          Ce module recense qui fait de la publicité dans vos pays, et confronte ces annonceurs à leur situation
          fiscale.
        </p>
        <h3>Annonceurs</h3>
        <p>Chaque annonceur porte <strong>deux statuts indépendants</strong>, et la distinction est essentielle :</p>
        <ul>
          <li>
            <strong>le statut fiscal</strong> décrit l'entreprise : <em>Non vérifié</em>, <em>Enregistré</em>,{' '}
            <em>Non enregistré</em>, <em>En vérification</em>. C'est un fait à établir ;
          </li>
          <li>
            <strong>le statut de vérification</strong> décrit votre travail sur le dossier. C'est l'avancement.
          </li>
        </ul>
        <p>
          Un annonceur <em>Non enregistré</em> au fiscal et <em>Nouveau</em> en vérification, c'est un dossier chaud que
          personne n'a encore ouvert. Le compteur du menu les compte.
        </p>
        <h3>Bannières sur les sites</h3>
        <p>
          À la différence des annonceurs, trouvés par recherche, les bannières sont repérées en visitant une liste de
          sites cibles — actualités, forums, petites annonces, e-commerce, blogs, portails. Vous gérez cette liste ici
          même, un par un ou par import.
        </p>
        <h3>Campagnes et requêtes</h3>
        <p>
          <strong>Campagnes de scan</strong> est l'historique : ce qui a été cherché, quand, dans quel pays, et ce que
          ça a donné.
        </p>
        <p>
          <strong>Requêtes surveillées</strong> est la liste des formules cherchées, classées par secteur. Trois pages
          de résultats sont lues par requête et par pays. Le secteur <strong>Jeux</strong> est marqué comme sensible :
          activité réglementée, à regarder en premier.
        </p>
        <p>
          Modifier les requêtes est réservé aux administrateurs. C'est le réglage qui a le plus d'effet sur ce que
          l'outil trouve, et il consomme du quota de recherche : une requête ajoutée à la légère coûte des recherches à
          toutes les autres.
        </p>
        <h3>Trois plateformes</h3>
        <p>
          Les résultats indiquent leur provenance — <strong>Google Ads</strong>, <strong>Meta</strong> ou{' '}
          <strong>Bannières</strong> — et s'il s'agissait d'une annonce payante, d'un résultat naturel ou d'une
          bannière. La collecte Meta passe par un prestataire tiers ; s'il n'est pas configuré sur le serveur, un
          bandeau le signale et seule la collecte Meta manque.
        </p>
      </>
    ),
  },
  {
    id: 'paris',
    titleKey: 'guide.s.betting',
    body: (
      <>
        <p>
          Le module le plus fourni, parce que la question est la plus délicate : un site de paris accepte-t-il un
          paiement là où la loi l'interdit ? On ne peut pas y répondre depuis un bureau : il faut aller au bout du
          parcours de paiement.
        </p>
        <h3>Tests de paiement</h3>
        <p className="guide-callout guide-callout-warn">
          <strong>Le test s'arrête avant le paiement.</strong> Deux profondeurs seulement : jusqu'au choix du moyen de
          paiement, ou jusqu'à la demande de paiement — <strong>avant la saisie du code PIN</strong>. Aucun test ne
          valide une transaction. Devant un écran de code PIN, le test est terminé : notez ce que vous voyez et
          arrêtez-vous là.
        </p>
        <p>
          Chaque test porte un numéro et indique s'il a été mené par un <strong>robot</strong> ou par un{' '}
          <strong>agent</strong>. Son déroulé passe par cinq états : en attente, parcours en cours, parcours terminé,
          parcours bloqué, parcours en échec. Un parcours bloqué n'est pas un échec de votre part — c'est souvent le
          site qui refuse le trafic.
        </p>
        <p>
          Au fil du parcours, vous consignez les étapes, les adresses visitées, les moyens de paiement proposés, les
          agrégateurs rencontrés, les domaines tiers, le nom du bénéficiaire, le montant, le texte du SMS reçu. Vous
          pouvez joindre des preuves. Un <strong>ticket PDF</strong> est générable à tout moment : c'est la pièce à
          verser au dossier.
        </p>
        <p><strong>Qualifier le résultat</strong> se fait en deux temps. D'abord la nature du manquement :</p>
        <Table
          head={['Manquement', 'Ce qu’il désigne']}
          rows={[
            ['Agrégateur non autorisé', 'L’encaissement passe par un intermédiaire sans accord'],
            ['Service interdit dans le pays', 'L’activité elle-même est prohibée là où le test a été mené'],
            ['Opérateur sans licence', 'Le site opère sans autorisation de jeu'],
            ['Moyen de paiement sans autorisation', 'Notre marque est acceptée sans contrat pour cet usage'],
            ['Publicité non conforme', 'Le manquement est dans la communication'],
            ['Problème technique', 'Le test n’a pas abouti pour une raison matérielle'],
          ]}
        />
        <p>
          Puis la gravité : <em>Haute</em>, <em>Moyenne</em>, <em>Faible</em>, <em>Problème technique</em>, ou{' '}
          <em>Conforme</em>. Un test non encore qualifié s'affiche « À qualifier ».
        </p>
        <h3>Rechercher des sites</h3>
        <p><strong>Deux sources, de coûts très différents</strong> :</p>
        <ul>
          <li><strong>la recherche web</strong> consomme le quota mensuel de recherches, une par modèle envoyé ;</li>
          <li><strong>les publicités</strong> relisent les annonceurs déjà collectés et ne coûtent rien.</li>
        </ul>
        <p>
          L'écran annonce ce qu'une passe va dépenser <strong>avant</strong> de la lancer, et ce qu'elle a dépensé
          après. Regardez ce chiffre : le quota est mensuel et se vide vite.
        </p>
        <p>
          Une recherche n'écrit rien en base. Elle rend des candidats, que vous importez ensuite en deux temps — un
          plan d'abord, que vous confirmez. Rien ne se crée dans votre dos.
        </p>
        <h3>À qualifier</h3>
        <p>
          La file d'apprentissage de l'outil : les bénéficiaires et les domaines aperçus pendant les tests que le
          référentiel ne reconnaît pas encore. Chaque décision enrichit le référentiel, et le prochain test reconnaîtra
          ce que vous venez de nommer. Un quart d'heure passé ici se rembourse sur tous les tests suivants.
        </p>
        <h3>Services et référentiels</h3>
        <p>
          <strong>Services de paris</strong> est la liste des sites suivis, avec leurs comptes de test. La gestion des
          comptes est réservée aux administrateurs, consultation comprise — ce sont des identifiants.
        </p>
        <p>
          <strong>Référentiels</strong> rassemble les opérateurs télécoms et les agrégateurs de paiement, chacun avec un
          statut : <em>Autorisé</em>, <em>Non autorisé</em>, ou <em>À qualifier</em>. Un agrégateur laissé à
          « À qualifier » ne déclenchera jamais de manquement : c'est là que se décide la portée réelle de la
          surveillance.
        </p>
      </>
    ),
  },
  {
    id: 'traiter-une-detection',
    titleKey: 'guide.s.triage',
    body: (
      <>
        <p>
          Les trois files — usurpation de marques, marchands non déclarés, paris & jeux — partagent{' '}
          <strong>le même circuit</strong>. C'est volontaire : vous n'avez pas à réapprendre un jeu de statuts à chaque
          onglet.
        </p>
        <TriageFlow />
        <p>
          Un dossier <em>Nouveau</em> peut aussi être pris en main directement, ou écarté d'emblée si l'erreur est
          manifeste.
        </p>
        <p>
          <strong>Le motif est obligatoire pour clore.</strong> Les quatre états teintés sur le schéma n'acceptent pas
          un passage sans commentaire écrit. Ce n'est pas une formalité : c'est ce commentaire qui, dans six mois,
          expliquera la décision à quelqu'un qui n'était pas là. Écrivez ce que vous avez constaté, pas ce que vous avez
          fait.
        </p>
        <p>
          <strong>Les sauts sont refusés.</strong> On ne passe pas de <em>Nouveau</em> à <em>Archivé</em>. Si une
          transition vous est refusée, ce n'est pas un bogue : l'étape intermédiaire manque.
        </p>
        <p>
          <strong>L'attribution.</strong> Vous pouvez confier une détection à quelqu'un, ou la remettre dans la file
          commune sans changer son statut. Deux choses à savoir : le champ est libre, donc{' '}
          <strong>une faute de frappe dans un nom ne sera pas signalée</strong>, et l'attribution{' '}
          <strong>ne prévient personne</strong>. Prévenez la personne vous-même.
        </p>
        <p>
          <strong>Le traitement en masse</strong> applique un même statut à plusieurs lignes d'un coup, jusqu'à 500.
          Chaque ligne garde malgré tout sa propre entrée au journal.
        </p>
        <p>
          <strong>Le journal.</strong> Chaque détection porte l'historique de son traitement : état de départ, état
          d'arrivée, motif, auteur, horodatage. Rien n'y est jamais réécrit ni effacé, et l'auteur est pris dans votre
          session — vous ne pouvez pas agir sous le nom d'un autre. C'est cette piste qui rend un dossier opposable.
        </p>
        <p>
          <strong>En retard.</strong> Une détection ouverte depuis plus de <strong>15 jours</strong> est comptée comme
          en retard. Le résumé de chaque file affiche ce chiffre, ainsi que le nombre de lignes que personne n'a prises.
        </p>
        <p className="guide-callout">
          <strong>Tous les rôles peuvent faire tout cela</strong>, agents compris. Confirmer une infraction, l'escalader
          ou l'archiver ne demande aujourd'hui aucun rang particulier. Si votre organisation veut réserver ces décisions
          à un responsable, c'est une règle à tenir entre vous — l'outil ne l'imposera pas.
        </p>
      </>
    ),
  },
  {
    id: 'configuration',
    titleKey: 'guide.s.configuration',
    body: (
      <>
        <h3>Modèles de recherche</h3>
        <p>
          C'est le réglage qui commande tout le reste. Les marques surveillées ne sont pas écrites dans le code : elles
          sont ici, et une marque ajoutée ce matin est prise en compte au prochain scan.
        </p>
        <p>
          Chaque marque se range dans une catégorie — <strong>Interne</strong> pour InTouch,{' '}
          <strong>Partenaire</strong> pour un opérateur, <strong>Client tiers</strong> pour une marque dont vous
          surveillez l'usurpation pour le compte d'un client.
        </p>
        <Table
          head={['Champ', 'À quoi il sert']}
          rows={[
            ['Nom', 'Le nom lisible, repris dans les alertes'],
            ['Termes de recherche', 'Les chaînes cherchées dans les noms de domaine'],
            ['Domaines officiels', 'Vos propres domaines, exclus des alertes. Un oubli ici et vous vous signalez vous-même'],
            ['Typosquats connus', 'Les variantes déjà vues, en plus de celles que l’outil génère seul'],
            ['Pays ciblés', 'Priorise les alertes. Vide signifie « partout »'],
            ['Modèles de recherche web', 'Les formules du scan mondial, une par ligne'],
          ]}
        />
        <p>
          Dans les modèles de recherche web, <code>{'{brand}'}</code> est remplacé par le nom de la marque. « payer avec
          {' {brand} '}» devient « payer avec InTouch ». Ajoutez les formules que vos clients emploient réellement, dans
          leur langue.
        </p>
        <p>
          Créer, modifier ou supprimer une marque est réservé au super admin — c'est le réglage le plus structurant de
          l'outil.
        </p>
        <p>
          <strong>Les marchands autorisés.</strong> Chaque carte de marque se déplie sur la liste des marchands
          autorisés pour <strong>cette marque précisément</strong>, avec une référence de contrat facultative. Un
          marchand peut être autorisé Wave et pas InTouch : c'est exactement ce que le scan mondial vérifie. La même
          liaison se gère depuis la fiche d'un marchand, côté marques — les deux écrans agissent sur la même donnée.
        </p>
        <h3>Règles pays</h3>
        <p>La matrice de ce qui est permis, activité par activité et pays par pays. Quatre niveaux :</p>
        <ul>
          <li><strong>Autorisé</strong> — rien à signaler ;</li>
          <li><strong>Sous conditions</strong> — légal avec une licence ou des mentions obligatoires ;</li>
          <li><strong>Zone grise</strong> — non interdit, mais à surveiller ;</li>
          <li><strong>Interdit</strong> — l'activité est prohibée dans ce pays.</li>
        </ul>
        <p>
          Ces niveaux pèsent directement dans le score de risque d'un crawl : une activité <strong>interdite</strong>{' '}
          ajoute 100 points à elle seule, ce qui place le rapport en critique quoi qu'il arrive. La modification est
          réservée aux administrateurs, et mérite de l'être : changer une règle sans référence légale documentable
          revient à fabriquer une infraction, ou à en effacer une.
        </p>
      </>
    ),
  },
  {
    id: 'administration',
    titleKey: 'guide.s.administration',
    body: (
      <>
        <h3>Utilisateurs</h3>
        <p>
          Visible des administrateurs seulement. Un compte se crée avec six éléments : nom, adresse courriel, mot de
          passe initial, rôle, <strong>pays autorisés</strong> et <strong>onglets autorisés</strong>.
        </p>
        <p className="guide-callout">
          Pour les deux listes de cases, <strong>ne rien cocher ouvre tout</strong> ce que l'organisation permet. Si
          vous voulez restreindre quelqu'un à un seul pays, cochez ce pays — ne laissez pas la liste vide en pensant
          restreindre.
        </p>
        <p><strong>Trois règles encadrent qui peut agir sur qui</strong>, et elles s'appliquent même à un administrateur :</p>
        <ol>
          <li>On n'agit que sur un compte de <strong>sa propre organisation</strong>.</li>
          <li>On n'agit que sur un compte de <strong>rang strictement inférieur</strong>. Un administrateur ne peut donc pas modifier un autre administrateur.</li>
          <li>On ne peut <strong>pas supprimer son propre compte</strong>.</li>
        </ol>
        <p>
          Seul le super admin crée ou promeut des propriétaires et des super admins, et seul lui peut déplacer un compte
          d'une organisation à l'autre.
        </p>
        <p>
          Un compte se <strong>désactive</strong> plutôt que de se supprimer quand la personne part : l'historique de
          ses décisions reste lisible dans les journaux.
        </p>
        <p>
          <strong>Réinitialiser un mot de passe</strong> se fait en modifiant le compte. Laissez le champ vide pour ne
          pas y toucher. Comme l'interface n'offre pas encore à chacun de changer le sien, c'est aujourd'hui le seul
          chemin — communiquez le nouveau mot de passe de vive voix, pas par écrit.
        </p>
        <h3>Organisations</h3>
        <p>
          Réservé au super admin. Une organisation porte son nom, un plan, les pays assignés et les modules souscrits,
          avec un plafond de pays affiché pendant la saisie. Les deux listes ne peuvent pas être vides.
        </p>
        <p>
          Ces deux réglages sont le plafond de tout le reste : un utilisateur ne peut jamais voir un pays ou un module
          que son organisation n'a pas. Si quelqu'un se plaint de ne pas voir un écran alors que son compte semble
          correct, vérifiez l'organisation avant le compte.
        </p>
      </>
    ),
  },
  {
    id: 'roles',
    titleKey: 'guide.s.roles',
    body: (
      <>
        <p>
          Quatre rôles, mais en pratique <strong>deux niveaux de droits</strong> : ce qui est ouvert à tous, et ce qui
          demande d'être administrateur. Chaque rôle ajoute aux précédents.
        </p>
        <Table
          head={['Rôle', 'Ce qu’il ajoute']}
          rows={[
            ['Agent', 'Consulter et exporter tout son périmètre · mener et qualifier les tests de paiement · créer, modifier, suspendre et réactiver un marchand · statuer sur un annonceur · traiter les détections de bout en bout, attribution et clôture comprises'],
            ['Administrateur', 'Lancer tous les scans · gérer le vocabulaire de recherche et les requêtes · décider des candidats · gérer les autorisations marchand × marque · résilier et supprimer · modifier les règles pays · accéder aux comptes de test · gérer les utilisateurs de son organisation'],
            ['Propriétaire', 'Rien de plus en pratique. Son rang supérieur le rend seulement intouchable par un administrateur, et lui permet d’agir sur les administrateurs de son organisation'],
            ['Super admin', 'Créer et modifier les marques surveillées · gérer les organisations · voir tous les pays et tous les modules, sans aucun filtre'],
          ]}
        />
      </>
    ),
  },
  {
    id: 'avant-de-signaler',
    titleKey: 'guide.s.beforeReporting',
    body: (
      <>
        <p>Six comportements surprennent souvent, et aucun n'est une panne.</p>
        <ol>
          <li><strong>Une liste de cases à cocher vide ouvre tout.</strong> Pays comme onglets : c'est le plafond de l'organisation qui s'applique.</li>
          <li><strong>Vous ne pouvez pas changer votre mot de passe vous-même.</strong> L'écran n'existe pas encore ; passez par un administrateur.</li>
          <li><strong>Attribuer une détection ne prévient personne</strong>, et le nom saisi n'est pas vérifié.</li>
          <li><strong>Le scan mondial remonte tout comme « inconnu »</strong> tant qu'aucune autorisation marchand × marque n'est enregistrée. Un bandeau le dit.</li>
          <li><strong>Un agrégateur laissé à « À qualifier » ne déclenchera jamais de manquement.</strong> Les référentiels incomplets rendent la surveillance muette, pas bruyante.</li>
          <li><strong>Les recherches ont un quota mensuel.</strong> Un scan qui ne rend rien en fin de mois peut simplement l'avoir épuisé.</li>
        </ol>
        <p>
          En revanche, méritent d'être signalés à l'équipe technique : un nombre de travaux <strong>échoués</strong> qui
          grimpe sur la vue d'ensemble, une capture d'écran manquante sur un rapport, ou un refus de transition que le
          schéma du circuit autorise pourtant.
        </p>
      </>
    ),
  },
]
